/** @jsxImportSource @emotion/react */
import {
  FormProvider,
  useController,
  useFieldArray,
  useForm,
} from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useSelector } from "react-redux";
import { toastr } from "react-redux-toastr";
import { css } from "@emotion/react";
import * as authSelectors from "../auth/authSelectors";
import { useEffect } from "react";
import {
  SalesChannelIntegration,
  SalesChannelFormData,
  SalesChannelPlatform,
} from "./models/sales-channel";
import {
  useCreateSalesChannel,
  useCreateSalesChannelEgressConnection,
  useDeleteSalesChannelEgressConnection,
  useGetSalesChannel,
  useGetSalesChannelEgressConnections,
  useUpdateSalesChannel,
  useUpdateSalesChannelEgressConnection,
} from "./service/shop-api";
import { useGetIntegrations } from "../integrations/service/integrations-api";
import { Drawer } from "../../components/Drawer";
import { ButtonAction } from "../../components/NewButtons";
import { ActionSpinner } from "../../components/ActionSpinner";
import { StyledForm } from "../../components/StyledForm";
import { TextInput } from "../../components/forms/inputs/TextInput";
import { SelectInput } from "../../components/forms/inputs/SelectInput";
import { DrawerSubHeader } from "../../components/DrawerSubHeader";
import { Divider } from "../../components/Divider";
import { Shop } from "./model/ShopSuccessResponse";
import { StyledInfoBox } from "../../components/StyledInfoBox";
import { SelectField } from "../../components/forms/fields/SelectField";
import { StyledText } from "../../assets/typography/typography";
import { colors } from "../../components/colors";
import { TextField } from "../../components/forms/fields/TextField";
import { getErrorMessage, useHandleFetchError } from "../../error-handler";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  salesChannelId?: string;
  shop?: Shop;
};

type PlatformOption = {
  value: SalesChannelPlatform;
  label: string;
};

export const platformOptions: PlatformOption[] = [
  {
    value: "Foodora",
    label: "Foodora",
  },
  {
    value: "JET",
    label: "Just Eat",
  },
  {
    value: "Wolt",
    label: "Wolt",
  },
];

const selectorWrapperStyle = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  width: 100%;
`;

export const MutateSalesChannelDrawer = ({
  isOpen,
  onClose,
  salesChannelId,
  shop,
}: Props) => {
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );

  const validationSchema = yup.object({
    platform: yup.string().test("platform", "Platform is required", (value) => {
      if (salesChannelId) {
        return true;
      }
      return !!value;
    }),
    name: yup.string().required("Name is required"),
    menuPushIntegration: yup
      .object({
        id: yup.string().required("Choose integration to continue"),
        reference: yup
          .string()
          .required(
            "Enter the integration reference here. This is what external partners will need to integrate to HeapsGo."
          ),
      })
      .nullable(),
    orderEgressIntegrations: yup
      .array(
        yup.object({
          description: yup.string().required("Description is required"),
          reference: yup.string().optional(),
          trigger: yup.string().oneOf(["ready", "manufacturing"]),
          integrationId: yup.string().required("Integration is required"),
          integrationName: yup.string().optional(),
        })
      )
      .min(0),
  });

  const methods = useForm<SalesChannelFormData>({
    resolver: yupResolver(validationSchema),
  });

  const {
    data: salesChannel,
    isLoading,
    error: salesChannelError,
  } = useGetSalesChannel(organizationKeyName, salesChannelId);

  const { mutateAsync: updateSalesChannel } = useUpdateSalesChannel(
    salesChannelId,
    shop?.id
  );

  const { mutateAsync: createSalesChannel } = useCreateSalesChannel();

  const {
    mutateAsync: createSalesChannelEgressConnections,
  } = useCreateSalesChannelEgressConnection(salesChannelId);

  const {
    mutateAsync: updateSalesChannelEgressConnections,
  } = useUpdateSalesChannelEgressConnection(salesChannelId);

  const {
    mutateAsync: deleteSalesChannelEgressConnection,
  } = useDeleteSalesChannelEgressConnection(salesChannelId);

  const {
    data: integrations,
    isLoading: isLoadingIntegrations,
    error: integrationsError,
  } = useGetIntegrations(organizationKeyName);

  const {
    data: egressConnections,
    isLoading: isLoadingEgressConnections,
    error: egressConnectionsError,
  } = useGetSalesChannelEgressConnections(organizationKeyName, salesChannelId);

  const handleOnClose = () => {
    methods.reset();
    onClose();
  };

  const { fields, replace } = useFieldArray({
    control: methods.control,
    name: "orderEgressIntegrations",
  });

  const {
    field: { onChange, ref, ...rest },
    fieldState,
  } = useController({
    name: "name",
    control: methods.control,
  });

  const onSubmit = async (data: SalesChannelFormData) => {
    if (!shop?.id) {
      throw new Error("Shop id is required");
    }
    const { orderEgressIntegrations, platform, ...rest } = data;
    if (rest.menuPushIntegration !== null && !rest.menuPushIntegration.id) {
      rest.menuPushIntegration.id = null;
    }

    if (!!rest.orderIngestIntegration && !rest.orderIngestIntegration?.id) {
      rest.orderIngestIntegration.id = null;
    }

    let salesChannelId_ = salesChannelId;

    try {
      if (!salesChannelId_) {
        salesChannelId_ = await createSalesChannel({
          data: {
            name: data.name,
            platform,
            shopId: shop?.id,
          },
          organizationKeyName,
        });
      }

      if (!salesChannelId_) {
        throw new Error("Sales channel id is not found");
      }

      const orderEgressIntegressIntegrationsToUpdate = (
        orderEgressIntegrations || []
      ).filter((integration) => {
        return !!integration.egressConnectionId;
      });

      const orderEgressIntegressIntegrationsToCreate = (
        orderEgressIntegrations || []
      ).filter((integration) => {
        return !integration.egressConnectionId;
      });

      const orderEgressIntegressIntegrationsToDelete = (
        egressConnections || []
      ).filter((egressConnection) => {
        return !(orderEgressIntegrations || []).find(
          (integration) =>
            integration.egressConnectionId === egressConnection.id
        );
      });

      await Promise.all([
        orderEgressIntegressIntegrationsToDelete.map(
          async (egressConnection) => {
            return deleteSalesChannelEgressConnection({
              organizationKeyName,
              data: {
                id: egressConnection.id,
                salesChannelId: salesChannelId_!,
              },
            });
          }
        ),
        orderEgressIntegressIntegrationsToCreate.map(async (integration) => {
          return createSalesChannelEgressConnections({
            organizationKeyName,
            data: {
              description: integration.description,
              integrationId: integration.integrationId,
              reference: integration.reference,
              salesChannelId: salesChannelId_!,
              trigger: integration.trigger,
            },
          });
        }),
        orderEgressIntegressIntegrationsToUpdate.map(async (integration) => {
          return updateSalesChannelEgressConnections({
            organizationKeyName,
            data: {
              id: integration.egressConnectionId!,
              description: integration.description,
              integrationId: integration.integrationId,
              reference: integration.reference,
              salesChannelId: salesChannelId_!,
              trigger: integration.trigger,
            },
          });
        }),
      ]);
      await updateSalesChannel({
        data: {
          name: data.name,
          platform: data.platform,
          menuPushIntegration: {
            id: data.menuPushIntegration?.id || null,
            reference: data.menuPushIntegration?.reference || "",
            name: data.menuPushIntegration?.name || "",
          },
          orderIngestIntegration: {
            id: data.orderIngestIntegration?.id || null,
            reference: data.orderIngestIntegration?.reference || "",
            name: data.orderIngestIntegration?.name || "",
          },
        },
        organizationKeyName,
        id: salesChannelId_,
      });

      toastr.success("Success", "Sales channel successfully updated");
      handleOnClose();
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error);
      toastr.error("Error", errorMessage);
    }
  };

  const platform = salesChannelId
    ? salesChannel?.platform
    : methods.watch("platform");

  const menuPushIntegrationOptions = integrations
    ?.filter(
      (integration) =>
        integration.capabilities?.includes("menu_push") &&
        integration.platform === platform
    )
    .map((integration) => ({
      value: integration.id,
      label: integration.name,
    }));

  menuPushIntegrationOptions?.unshift({ value: "", label: "None" });

  const orderIngestIntegrationOptions = integrations
    ?.filter(
      (integration) =>
        integration.capabilities?.includes("order_ingest") &&
        integration.platform === platform
    )
    .map((integration) => ({
      value: integration.id,
      label: integration.name,
    }));

  orderIngestIntegrationOptions?.unshift({ value: "", label: "None" });

  const orderEgressIntegrationOptions = integrations
    ?.filter(
      (integration) =>
        integration.capabilities?.includes("order_egress") ||
        integration.capabilities?.includes("gift_card_redeem") ||
        integration.capabilities?.includes("add_to_order")
    )
    .map((integration) => ({
      value: integration.id,
      label: integration.name,
    }));

  useHandleFetchError(integrationsError);
  useHandleFetchError(egressConnectionsError);
  useHandleFetchError(salesChannelError);

  useEffect(() => {
    if (
      isLoading ||
      !salesChannel ||
      !shop?.id ||
      isLoadingIntegrations ||
      !integrations ||
      isLoadingEgressConnections ||
      !egressConnections
    ) {
      return;
    }

    if (!salesChannelId) {
      return;
    }

    let orderIngestIntegration: SalesChannelIntegration | null = null;
    let menuPushIntegration: SalesChannelIntegration | null = null;

    const firstOrderIngestIntegration = integrations.find(
      (integration) =>
        integration.capabilities?.includes("order_ingest") &&
        integration.platform === salesChannel?.platform
    );

    const firstMenuPushIntegration = integrations.find(
      (integration) =>
        integration.capabilities?.includes("menu_push") &&
        integration.platform === salesChannel?.platform
    );

    if (firstOrderIngestIntegration) {
      orderIngestIntegration = {
        id: "",
        reference: shop?.id,
      };
    }

    if (firstMenuPushIntegration) {
      menuPushIntegration = {
        id: "",
        reference: shop?.id,
      };
    }

    if (salesChannel.orderIngestIntegration) {
      orderIngestIntegration = salesChannel.orderIngestIntegration;
    }
    if (salesChannel.menuPushIntegration) {
      menuPushIntegration = salesChannel.menuPushIntegration;
    }

    methods.reset({
      menuPushIntegration,
      orderIngestIntegration,
      name: salesChannel?.name,
      orderEgressIntegrations: egressConnections.map((egressConnection) => ({
        reference: egressConnection.reference,
        description: egressConnection.description,
        integrationId: integrations?.find(
          (integration) => integration.name === egressConnection.integrationName
        )?.id,
        integrationName: egressConnection.integrationName,
        egressConnectionId: egressConnection.id,
        trigger: egressConnection.trigger || "",
      })),
    });
  }, [
    isLoading,
    salesChannel,
    salesChannelId,
    methods,
    shop?.id,
    integrations,
    isLoadingIntegrations,
    egressConnections,
    isLoadingEgressConnections,
  ]);

  return (
    <Drawer
      title={`${salesChannelId ? "Update" : "Create"} sales channel`}
      width={500}
      styles={{
        body: {
          padding: "0px",
        },
      }}
      buttons={
        <ButtonAction
          onClick={methods.handleSubmit(onSubmit)}
          type="button"
          disabled={methods.formState.isSubmitting}
        >
          {methods.formState.isSubmitting ? <ActionSpinner /> : "Save"}
        </ButtonAction>
      }
      open={isOpen}
      onClose={handleOnClose}
    >
      <FormProvider {...methods}>
        <StyledForm>
          <div css={selectorWrapperStyle}>
            {!salesChannelId && (
              <SelectInput
                name="platform"
                label="Platform"
                options={platformOptions}
                helperText="Choose the platform for the sales channel"
                onChange={(value) => onChange(`${shop?.title} - ${value}`)}
              />
            )}
            <TextField
              label="Name"
              disabled={!methods.watch("platform") && !salesChannelId}
              innerRef={ref}
              errorMessage={fieldState.error && fieldState.error?.message}
              placeholder="Name of sales channel"
              onChange={(e) => onChange(e.target.value)}
              helperText={
                !methods.watch("platform") && !salesChannelId
                  ? "Please select platform before proceeding with name"
                  : "Enter the name of this sales channel"
              }
              {...rest}
            />
          </div>
          <Divider />
          {((!!methods.watch("platform") && !salesChannelId) ||
            salesChannelId) && (
            <>
              <div css={selectorWrapperStyle}>
                <DrawerSubHeader
                  header="Menu push integration"
                  subtext="Select a menu push integration. Will allow you to push your menu to an external third party."
                />
                {menuPushIntegrationOptions?.length === 1 && (
                  <StyledInfoBox
                    title="No menu push integrations available"
                    subtitle="Contact HeapsGo if you think this is a mistake"
                  />
                )}
                {menuPushIntegrationOptions &&
                  menuPushIntegrationOptions.length > 1 && (
                    <>
                      <SelectInput
                        name="menuPushIntegration.id"
                        label="Menu push integration"
                        options={menuPushIntegrationOptions || []}
                        helperText="Choose the menu push integration you want to use for this sales channel"
                      />
                      <TextInput
                        label="Integration reference"
                        name="menuPushIntegration.reference"
                        helperText="Enter the reference for the menu push integration you want to use for this sales channel. You should give this to the external service provider to set up the integration."
                      />
                    </>
                  )}
              </div>
              <Divider />
              <div css={selectorWrapperStyle}>
                <DrawerSubHeader
                  header="Incoming orders integration"
                  subtext="Select an incoming orders integration. Will allow you to handle orders from external sales channels on your restaurant tablet."
                />
                {orderIngestIntegrationOptions?.length === 1 && (
                  <StyledInfoBox
                    title="No incoming orders integrations available"
                    subtitle="Contact HeapsGo if you think this is a mistake"
                  />
                )}
                {orderIngestIntegrationOptions &&
                  orderIngestIntegrationOptions.length > 1 && (
                    <>
                      <SelectInput
                        name="orderIngestIntegration.id"
                        label="Incoming orders integration"
                        options={orderIngestIntegrationOptions || []}
                        isDisabled={orderIngestIntegrationOptions?.length === 1}
                        helperText="Choose the incoming orders integration you want to use for this sales channel"
                      />
                      <TextInput
                        label="Integration reference"
                        name="orderIngestIntegration.reference"
                        disabled={orderIngestIntegrationOptions?.length === 1}
                        helperText="Enter the reference for the order ingest integration you want to use for this sales channel. You should give this to the external service provider to set up the integration."
                      />
                    </>
                  )}
              </div>
              <Divider />
              <div css={selectorWrapperStyle}>
                <DrawerSubHeader
                  header="Outgoing integrations"
                  subtext="Select outgoing integrations. Will allow you to send orders and other data to external sales channels."
                />
                {orderEgressIntegrationOptions?.length === 0 && (
                  <StyledInfoBox
                    title="No outgoing integrations available"
                    subtitle="Contact HeapsGo if you think this is a mistake"
                  />
                )}
                {orderEgressIntegrationOptions &&
                  orderEgressIntegrationOptions.length > 0 && (
                    <>
                      <SelectField
                        isMulti
                        closeMenuOnSelect={false}
                        placeholder="Start typing to search for outgoing orders integrations..."
                        errorMessage={
                          methods.getFieldState(
                            "orderEgressIntegrations",
                            methods.formState
                          ).error?.message
                        }
                        onChange={(value) => {
                          const existingOrderEgressIntegrations = methods.watch(
                            "orderEgressIntegrations"
                          );
                          const newOrderEgressIntegrations: SalesChannelFormData["orderEgressIntegrations"] = (value as {
                            value: string;
                            label: string;
                          }[]).map((x) => ({
                            description:
                              existingOrderEgressIntegrations?.find(
                                (ei) => ei.integrationId === x.value
                              )?.description || "",
                            integrationId: x.value,
                            integrationName: x.label,
                            trigger: "ready",
                            reference:
                              existingOrderEgressIntegrations?.find(
                                (ei) => ei.integrationId === x.value
                              )?.reference || "",
                          }));
                          replace(newOrderEgressIntegrations);
                        }}
                        options={orderEgressIntegrationOptions}
                        value={orderEgressIntegrationOptions?.filter(
                          (option) => {
                            return (
                              methods.watch("orderEgressIntegrations") || []
                            ).find(
                              (ing) => ing.integrationName === option.label
                            );
                          }
                        )}
                      />
                    </>
                  )}
              </div>
              <Divider />

              {fields.map((_, i) => {
                const formOrderEgressIntegration = methods.watch(
                  `orderEgressIntegrations.${i}`
                );

                return (
                  <div css={selectorWrapperStyle} key={i}>
                    <div
                      css={css`
                        display: flex;
                        flex-direction: column;
                        gap: 8px;
                      `}
                    >
                      <StyledText
                        className="bodyTextSemibold mb-0"
                        color={colors.textPrimary}
                      >
                        {formOrderEgressIntegration.integrationName}
                      </StyledText>
                      <StyledText
                        className="bodySubtextRegular mb-0"
                        color={colors.textSecondary}
                      >
                        Enter reference and description for this egress
                        integration
                      </StyledText>
                    </div>
                    <TextInput
                      label="Description"
                      placeholder="Enter a description for this integration"
                      name={`orderEgressIntegrations.${i}.description`}
                    />
                    <TextInput
                      label="Reference"
                      placeholder="Enter a reference for this integration"
                      name={`orderEgressIntegrations.${i}.reference`}
                    />
                    <SelectInput
                      label="Trigger"
                      name={`orderEgressIntegrations.${i}.trigger`}
                      options={[
                        { value: "ready", label: "Ready" },
                        { value: "manufacturing", label: "Cooking" },
                      ]}
                      helperText="Choose the trigger for when orders should be sent to external systems"
                    />
                  </div>
                );
              })}
            </>
          )}
        </StyledForm>
      </FormProvider>
    </Drawer>
  );
};
